"use client";

import { LogOut } from "lucide-react";
import { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";

import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { logout, getSupabaseSession } from "@/app/_module/services/auth";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { displayAgencyWithRole } from "@/app/_module/utils/agency-display";
import { getInitialsFromFullName } from "@/app/_module/utils/get-initials";
import InsuraCentralLogo from "@/app/_module/components/InsuraCentralLogo";
import { useDeviceContext } from "@/app/_module/contexts/DeviceDetectionContext";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";

import { userServices } from "../services/user-services";

interface Agency {
    id: string;
    name: string;
    description: string;
    contactEmail: string;
    isActive: boolean;
    parentAgencyId?: string | null;
    logoUrl?: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
}

interface AgencyWithRole {
    agency: Agency;
    role: string;
    title?: string;
    permissions: string[];
}

interface LoginResponse {
    user: {
        id: string;
        name: string;
        email: string;
        title: string;
        userType: string;
        profileImageUrl?: string;
        profile_image_url?: string;
    };
    agencies: AgencyWithRole[];
    isMultiAgencyUser: boolean;
}

interface AgencySelectionProps {
    loginData: LoginResponse;
    onBack: () => void;
    onSuccess: () => void;
    onStart?: () => void;
    /** Direct 130's address: a white-label agent with one agency goes straight in, no picker. */
    autoSelect?: boolean;
}

const AgencySelection = function ({ loginData, onBack, onSuccess, onStart, autoSelect }: AgencySelectionProps) {
    const { toast } = useToast();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { updateCurrentAgency } = useUserContext();
    const { getResponsiveMaxWidth } = useDeviceContext();
    const { branding, isWhiteLabeled } = useBranding();
    const [isLoading, setIsLoading] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false); // Prevent duplicate calls
    const [hasNavigated, setHasNavigated] = useState(false); // Prevent duplicate navigation
    const [activeAgencyId, setActiveAgencyId] = useState<string | null>(null);
    const activeButtonRef = useRef<HTMLButtonElement | null>(null);

    // Debug: Check session on component mount and trigger agency selection mode
    useEffect(() => {
        const init = async () => {
            // Trigger agency selection mode to disable Turnstile
            onStart?.();

            // Ensure session exists (no-op log)
            await getSupabaseSession();
            // Resolve the last-active agency id so we can pin/highlight the
            // agency the user is actually in (their primary).
            const id = await userServices.getActiveAgencyId();
            if (id) setActiveAgencyId(id);
        };
        void init();
    }, [onStart]);

    // The "primary" agency to pin to the top + make stand out. This is your
    // HOME base, not a sub you happen to manage — so a top-level agency you
    // own always wins over a sub-agency. (The last-active agency is surfaced
    // separately as a "Last signed in here" note, not by pinning.) This
    // guarantees owners like Brandon/Bruce see their main agency first
    // instead of a sub like "Hitmanz Inc".
    const OWNER_ROLES = useMemo(() => new Set(['owner', 'agency_owner', 'admin', 'agency_admin']), []);
    const primaryAgencyId = useMemo(() => {
        const list = loginData.agencies ?? [];
        if (list.length === 0) return null;

        // Founding (earliest-created) agency wins when several candidates tie.
        const earliest = (items: AgencyWithRole[]) =>
            items.slice().sort((a, b) =>
                new Date(a.agency.createdAt || 0).getTime() - new Date(b.agency.createdAt || 0).getTime()
            )[0];

        // 1. A top-level agency you lead (no parent) — your home base.
        const ownedTopLevel = list.filter(
            (a) => OWNER_ROLES.has((a.role || '').toLowerCase()) && !a.agency.parentAgencyId
        );
        if (ownedTopLevel.length) return earliest(ownedTopLevel).agency.id;

        // 2. Any agency you lead (e.g. you only own sub-agencies).
        const owned = list.filter((a) => OWNER_ROLES.has((a.role || '').toLowerCase()));
        if (owned.length) return earliest(owned).agency.id;

        // 3. Fall back: last-active agency, then first top-level, then first.
        if (activeAgencyId && list.some((a) => a.agency.id === activeAgencyId)) return activeAgencyId;
        const topLevel = list.find((a) => !a.agency.parentAgencyId);
        return (topLevel ?? list[0]).agency.id;
    }, [activeAgencyId, loginData.agencies, OWNER_ROLES]);

    useEffect(() => {
        // Auto-scroll highlighted agency into view if present
        if (activeButtonRef.current) {
            try { activeButtonRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch {}
        }
    }, [primaryAgencyId]);

    const handleAgencySelect = async (agencyId: string): Promise<boolean> => {
        // Prevent duplicate calls - critical fix for infinite loop
        if (isSelecting || isLoading || hasNavigated) {
            return false;
        }

        setIsSelecting(true);
        setIsLoading(true);
        
        try {
            // Find the selected agency data from loginData for fallback
            const selectedAgencyFromLogin = loginData.agencies.find(a => a.agency.id === agencyId);
            
            const response = await userServices.postAgency(
                agencyId,
                selectedAgencyFromLogin?.agency, // Pass agency data as fallback
                { silent: false }
            );

            // Create the agency object that matches the response structure
            const currentAgency = {
                agency: response.agency,
                role: response.agencyRole,
                permissions: response.agencyPermissions,
            };

            // Update the user context immediately with full agency data
            if (currentAgency.agency && currentAgency.role) {
              updateCurrentAgency?.({
                id: currentAgency.agency.id,
                name: currentAgency.agency.name,
                role: currentAgency.role,
                description: currentAgency.agency.description,
                contactEmail: currentAgency.agency.contactEmail,
                isActive: currentAgency.agency.isActive,
                parentAgencyId: currentAgency.agency.parentAgencyId,
                createdAt: currentAgency.agency.createdAt,
                updatedAt: currentAgency.agency.updatedAt,
                deletedAt: currentAgency.agency.deletedAt,
                permissions: currentAgency.permissions,
              });
              
              // Ensure dashboard reflects new agency immediately
              try { queryClient.invalidateQueries(); } catch {}

              // Simplified navigation - just call onSuccess and let parent handle navigation
              setHasNavigated(true);
              onSuccess();
              return true;
            } else {

              throw new Error('Agency data or role missing from response');
            }
        } catch (error) {
            let errorMessage = "Failed to select agency";
            if (error instanceof Error) {
                errorMessage = error.message;
            }

            toast({
                title: "Error",
                description: errorMessage,
                className: "max-w-sm bg-red-500 border border-gray-200 text-white shadow-lg",
            });
            return false;
        } finally {
            setIsLoading(false);
            setIsSelecting(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        onBack();
    };

    // One agency on a white-label address: select it the same way a click would. The ref keeps React's
    // double-run effects (dev) from posting twice; a failed select falls back to the picker.
    const onlyAgencyId = autoSelect && loginData.agencies.length === 1 ? loginData.agencies[0].agency.id : null;
    const [autoFailed, setAutoFailed] = useState(false);
    const autoStarted = useRef(false);
    useEffect(() => {
        if (!onlyAgencyId || autoStarted.current) return;
        autoStarted.current = true;
        void handleAgencySelect(onlyAgencyId).then((ok) => { if (!ok) setAutoFailed(true); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlyAgencyId]);

    if (onlyAgencyId && !autoFailed) {
        return (
            <div className="flex min-h-[46px] items-center justify-center gap-2 text-[15px] text-[#566465]" role="status">
                <LoadingSpinner size="md" /> Signing you in…
            </div>
        );
    }

    const userInitials = getInitialsFromFullName(
        loginData.user.name.split(' ')[0] || 'U',
        loginData.user.name.split(' ')[1] || 'S'
    );

    return (
        <div data-agency-select className={`w-full ${getResponsiveMaxWidth("lg")} mx-auto space-y-8`}>
            {/* Logo */}
            <div className="flex items-center gap-2">
                <InsuraCentralLogo size={32} />
                {isWhiteLabeled ? (
                <span className="text-3xl font-bold text-primary">{branding.brandName}</span>
              ) : (
                <span className="text-3xl font-bold">
                  <span className="text-[#0a2540]">Insura</span>
                  <span className="text-[#0096c7]">Central</span>
                </span>
              )}
            </div>

            {/* Welcome Section. `welcome-row` is a styling hook: on the native
                shell (.native-auth CSS) this row stacks — avatar centered ABOVE
                the greeting — because a side-by-side avatar next to a name that
                wraps to two lines reads lopsided on a phone. Web keeps the row. */}
            <div className="welcome-row flex items-center gap-3">
                <div className="welcome-avatar w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center overflow-hidden relative flex-shrink-0">
                    {(loginData.user.profileImageUrl || loginData.user.profile_image_url) ? (
                        <Image
                            src={loginData.user.profileImageUrl || loginData.user.profile_image_url || ''}
                            alt={loginData.user.name}
                            width={48}
                            height={48}
                            className="object-cover w-full h-full"
                            onError={(e) => {
                                // Fallback to initials if image fails to load
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.parentElement?.querySelector('.fallback-initials');
                                if (fallback) {
                                    (fallback as HTMLElement).style.display = 'flex';
                                }
                            }}
                        />
                    ) : null}
                    <span className={`fallback-initials text-lg font-semibold text-primary ${(loginData.user.profileImageUrl || loginData.user.profile_image_url) ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
                        {userInitials}
                    </span>
                </div>
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                        Welcome back, {loginData.user.name}
                    </h2>
                </div>
            </div>

            {/* Agency Selection Card. agency-panel is a styling hook: native flattens
                the panel (globals.css); the list keeps its own scroll. */}
            <div className="agency-panel bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Select your agency
                </h3>

                <div className="space-y-3 max-h-[296px] overflow-y-auto pr-1">
                    {loginData.agencies
                        .slice()
                        .sort((a, b) => {
                            // The primary agency (the one you're in) always pins to the top.
                            if (a.agency.id === primaryAgencyId && b.agency.id !== primaryAgencyId) return -1;
                            if (b.agency.id === primaryAgencyId && a.agency.id !== primaryAgencyId) return 1;
                            // Then main agencies (no parent) before sub-agencies.
                            if (!a.agency.parentAgencyId && b.agency.parentAgencyId) return -1;
                            if (a.agency.parentAgencyId && !b.agency.parentAgencyId) return 1;
                            // Both are same type, sort alphabetically by name
                            return a.agency.name.localeCompare(b.agency.name);
                        })
                        .map((agencyData) => {
                        const isPrimary = !!primaryAgencyId && agencyData.agency.id === primaryAgencyId;
                        const isLastUsed = !!activeAgencyId && agencyData.agency.id === activeAgencyId;
                        return (
                        <button
                            key={agencyData.agency.id}
                            ref={isPrimary ? activeButtonRef : undefined}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${isPrimary ? 'border-primary border-2 bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'border-gray-200 hover:border-primary/30 hover:bg-primary/5'}`}
                            disabled={isLoading || isSelecting || hasNavigated}
                            onClick={() => handleAgencySelect(agencyData.agency.id)}
                        >
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden relative">
                                {agencyData.agency.logoUrl ? (
                                    <Image
                                        src={agencyData.agency.logoUrl}
                                        alt={agencyData.agency.name}
                                        width={40}
                                        height={40}
                                        className="object-cover w-full h-full"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const fallback = e.currentTarget.parentElement?.querySelector('.fallback-initials');
                                            if (fallback) {
                                                (fallback as HTMLElement).style.display = 'flex';
                                            }
                                        }}
                                    />
                                ) : null}
                                <span className={`fallback-initials text-sm font-semibold text-primary ${agencyData.agency.logoUrl ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
                                    {agencyData.agency.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 text-left">
                                <p className={`font-medium ${isPrimary ? 'text-primary' : 'text-gray-900'}`}>
                                    {agencyData.agency.name}
                                </p>
                                <p className="text-xs font-semibold text-gray-500 mt-0.5">
                                    {agencyData.role.charAt(0).toUpperCase() + agencyData.role.slice(1)}
                                </p>
                                {agencyData.title && (
                                    <p className="text-xs text-gray-600 mt-0.5">
                                        {agencyData.title}
                                    </p>
                                )}
                                {isLastUsed && (
                                    <p className="text-[11px] text-primary/70 mt-0.5">
                                        Last signed in here
                                    </p>
                                )}
                            </div>
                        </button>
                    );})}
                </div>
            </div>

            {/* Logout Link */}
            <div className="text-center">
                <button
                    className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center justify-center gap-2 mx-auto"
                    onClick={handleLogout}
                >
                    <LogOut className="w-4 h-4" />
                    Log out
                </button>
            </div>
        </div>
    );
}

export default AgencySelection; 